import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import logo from '../../../../img//logo rio verde pdf.png';
import styles from './DesbasteTablePopup.module.css';
import { API_BASE_URL } from "../../../../config";
import { FaFilePdf, FaTrashAlt, FaExclamationTriangle } from 'react-icons/fa';
import { generateDesbasteReport } from './reportGeneratorDesbaste';

ChartJS.register(ArcElement, Tooltip, Legend);

const DesbasteTablePopup = ({ isOpen, onClose, imovelId }) => {
    const [desbasteData, setDesbasteData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const [itemToDeleteId, setItemToDeleteId] = useState(null);
    const [selectedItems, setSelectedItems] = useState([]);
    const navigate = useNavigate();

    const formatCurrency = (value) => {
        const number = parseFloat(String(value).replace(',', '.'));
        if (isNaN(number)) return "R$ 0,00";
        return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(number);
    };

    const formatNumber = (value) => {
        if (value === null || value === undefined) return '0';
        const number = parseFloat(String(value).replace(',', '.'));
        if (isNaN(number)) return '0';
        return parseInt(number, 10).toLocaleString('pt-BR');
    };

    const formatDate = (dateString) => {
        if (!dateString) return "N/A";
        return new Date(dateString).toLocaleDateString('pt-BR', { timeZone: 'UTC' });
    };

    const fetchDesbastes = useCallback(async () => {
        if (!imovelId) return;
        const token = localStorage.getItem('token');
        if (!token) {
            navigate('/');
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const response = await axios.get(
                `${API_BASE_URL}/api/imoveis/${imovelId}/desbastes`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            setDesbasteData(Array.isArray(response.data) ? response.data : []);
        } catch (error) {
            console.error("Erro ao buscar desbastes:", error);
            if (error.response) {
                 if (error.response.status === 401 || error.response.status === 400) {
                    localStorage.removeItem('token');
                    navigate('/');
                } else if (error.response.status === 404) {
                    setDesbasteData([]);
                } else {
                    setError("Não foi possível carregar os dados de desbaste.");
                }
            } else {
                setError("Erro de conexão ao buscar os dados.");
            }
        } finally {
            setLoading(false);
        }
    }, [imovelId, navigate]);

    useEffect(() => {
        if (isOpen) {
            fetchDesbastes();
        } else {
            setDesbasteData([]); setLoading(true); setError(null);
            setShowDeleteConfirmation(false); setItemToDeleteId(null); setSelectedItems([]);
        }
    }, [isOpen, fetchDesbastes]);

    const summaryData = useMemo(() => {
        if (desbasteData.length === 0) return null;

        const totais = desbasteData.reduce((acc, item) => {
            const totalLenha = (item.lenha || 0) * (item.preco_lenha || 0);
            const totalToretes = (item.toretes || 0) * (item.preco_toretes || 0);
            const total2025 = (item.toras_20_25cm || 0) * (item.preco_toras_20_25cm || 0);
            const total2533 = (item.toras_25_33cm || 0) * (item.preco_toras_25_33cm || 0);
            const total33 = (item.toras_acima_33cm || 0) * (item.preco_toras_acima_33cm || 0);

            acc.arvores += parseFloat(item.arvores_cortadas || 0);
            acc.receitaTotal += totalLenha + totalToretes + total2025 + total2533 + total33;
            acc.receita.lenha += totalLenha;
            acc.receita.toretes += totalToretes;
            acc.receita.toras2025 += total2025;
            acc.receita.toras2533 += total2533;
            acc.receita.toras33 += total33;
            
            return acc;
        }, { 
            arvores: 0, receitaTotal: 0, 
            receita: { lenha: 0, toretes: 0, toras2025: 0, toras2533: 0, toras33: 0 }
        });

        return {
            kpis: {
                receitaTotal: totais.receitaTotal,
                totalArvores: totais.arvores,
            },
            pieChartData: {
                labels: ['Lenha', 'Toretes', 'Toras 20-25cm', 'Toras 25-33cm', 'Toras +33cm'],
                datasets: [{
                    data: Object.values(totais.receita),
                    backgroundColor: ['#1E8449', '#27AE60', '#2ECC71', '#58D68D', '#82E0AA'],
                    borderColor: '#FFFFFF',
                    borderWidth: 2,
                }]
            }
        };
    }, [desbasteData]);

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { position: 'bottom', labels: { boxWidth: 12, font: { size: 11 } } }
        },
    };

    const handleSelectItem = (id) => { setSelectedItems(prev => prev.includes(id) ? prev.filter(itemId => itemId !== id) : [...prev, id]); };
    const handleSelectAll = (e) => {
        if (e.target.checked) { setSelectedItems(desbasteData.map(item => item.id)); } 
        else { setSelectedItems([]); }
    };
    const confirmDelete = (id) => { setItemToDeleteId(id); setShowDeleteConfirmation(true); };
    const cancelDelete = () => { setItemToDeleteId(null); setShowDeleteConfirmation(false); };
    
    const executeDelete = async () => {
        if (!itemToDeleteId) return;
        const token = localStorage.getItem('token');
        if (!token) {
            setError("Sessão expirada. Faça login novamente.");
            navigate('/');
            return;
        }
        try {
            const response = await axios.delete(
                `${API_BASE_URL}/api/imoveis/${imovelId}/desbastes/${itemToDeleteId}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (response.status === 200) {
                setDesbasteData(prevData => prevData.filter(desbaste => desbaste.id !== itemToDeleteId));
                setSelectedItems(prev => prev.filter(id => id !== itemToDeleteId));
            }
        } catch (err) {
            if (err.response && (err.response.status === 401 || err.response.status === 400)) {
                localStorage.removeItem('token');
                navigate('/');
            } else {
                console.error("Erro ao excluir desbaste:", err);
                setError(err.response?.data?.error || "Erro ao excluir o registro de desbaste.");
            }
        } finally {
            cancelDelete();
        }
    };
    
    const handleGenerateReport = async () => {
        const selectedData = desbasteData.filter(item => selectedItems.includes(item.id));
        await generateDesbasteReport({
            selectedData,
            imovelId,
            logo
        });
    };

    if (!isOpen) return null;

    return (
        <>
            {showDeleteConfirmation && (
                <div className={styles.deleteConfirmationOverlay} onClick={cancelDelete}>
                    <div className={styles.deleteConfirmationModal} onClick={(e) => e.stopPropagation()}>
                        <FaExclamationTriangle className={styles.warningIcon} />
                        <h3>Confirmar Exclusão</h3>
                        <p>Tem certeza que deseja excluir este registro? A ação não pode ser desfeita.</p>
                        <div className={styles.modalFooterStacked}>
                            <button className={styles.btnDangerStacked} onClick={executeDelete}><FaTrashAlt /> Confirmar Exclusão</button>
                            <button className={styles.btnLinkStacked} onClick={cancelDelete}>Cancelar</button>
                        </div>
                    </div>
                </div>
            )}
            
            <div className={styles.desbasteModalOverlay} onClick={onClose}>
                <div className={styles.desbasteModalContent} onClick={(e) => e.stopPropagation()}>
                    <header className={styles.desbasteModalHeader}>
                        <h2 className={styles.desbasteModalTitle}>Análise de Desbastes</h2>
                        <button className={styles.desbasteModalCloseBtn} onClick={onClose}>&times;</button>
                    </header>

                    {loading ? <p className={styles.desbasteLoadingMessage}>Carregando...</p> : 
                     error ? <p className={styles.desbasteErrorMessage}>{error}</p> : 
                     !summaryData ? <p className={styles.desbasteNoData}>Nenhum desbaste registrado.</p> :
                     (
                        <main className={styles.desbasteModalBody}>
                            <aside className={styles.summaryPanel}>
                                <div className={styles.kpiContainer}>
                                    <div className={styles.kpiCard}>
                                        <div className={styles.kpiLabel}>Receita Total Gerada</div>
                                        <div className={styles.kpiValue}>{formatCurrency(summaryData.kpis.receitaTotal)}</div>
                                    </div>
                                    <div className={styles.kpiCard}>
                                        <div className={styles.kpiLabel}>Total de Árvores Cortadas</div>
                                        <div className={styles.kpiValue}>{formatNumber(summaryData.kpis.totalArvores)}</div>
                                    </div>
                                </div>
                                <div className={styles.chartContainer}>
                                    <h3 className={styles.chartTitle}>Composição da Receita</h3>
                                    <Doughnut data={summaryData.pieChartData} options={chartOptions} />
                                </div>
                            </aside>

                            <section className={styles.tablePanel}>
                                <div className={styles.desbasteTableContainer}>
                                    <div className={styles.desbasteTableResponsive}>
                                        <table className={styles.desbasteTable}>
                                            <thead>
                                                <tr>
                                                    <th><input type="checkbox" onChange={handleSelectAll} checked={selectedItems.length === desbasteData.length && desbasteData.length > 0} ref={el => el && (el.indeterminate = selectedItems.length > 0 && selectedItems.length < desbasteData.length)} title="Selecionar Todos" /></th>
                                                    <th>Nº</th>
                                                    <th>Data</th>
                                                    <th>Árvores</th>
                                                    <th className={styles.thGroupVolume}>Lenha (st)</th>
                                                    <th className={styles.thGroupVolume}>Toretes (m³)</th>
                                                    <th className={styles.thGroupVolume}>20-25cm (m³)</th>
                                                    <th className={styles.thGroupVolume}>25-33cm (m³)</th>
                                                    <th className={styles.thGroupVolume}>+33cm (m³)</th>
                                                    <th className={styles.thGroupPreco}>P. Lenha</th>
                                                    <th className={styles.thGroupPreco}>P. Toretes</th>
                                                    <th className={styles.thGroupPreco}>P. 20-25</th>
                                                    <th className={styles.thGroupPreco}>P. 25-33</th>
                                                    <th className={styles.thGroupPreco}>P. +33</th>
                                                    <th className={styles.thGroupPreco}>V. Extração</th>
                                                    <th className={styles.thGroupTotal}>Total Lenha</th>
                                                    <th className={styles.thGroupTotal}>Total Toretes</th>
                                                    <th className={styles.thGroupTotal}>Total 20-25</th>
                                                    <th className={styles.thGroupTotal}>Total 25-33</th>
                                                    <th className={styles.thGroupTotal}>Total +33</th>
                                                    <th className={styles.thGroupTotal}>Total Geral</th>
                                                    <th>Ação</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {desbasteData.map((desbaste) => {
                                                    const totalLenha = desbaste.lenha * desbaste.preco_lenha;
                                                    const totalToretes = desbaste.toretes * desbaste.preco_toretes;
                                                    const totalToras2025cm = desbaste.toras_20_25cm * desbaste.preco_toras_20_25cm;
                                                    const totalToras2533cm = desbaste.toras_25_33cm * desbaste.preco_toras_25_33cm;
                                                    const totalTorasAcima33cm = desbaste.toras_acima_33cm * desbaste.preco_toras_acima_33cm;
                                                    const totalGeral = totalLenha + totalToretes + totalToras2025cm + totalToras2533cm + totalTorasAcima33cm;

                                                    return (
                                                        <tr key={desbaste.id} className={selectedItems.includes(desbaste.id) ? styles.selectedRow : ''}>
                                                            <td><input type="checkbox" checked={selectedItems.includes(desbaste.id)} onChange={() => handleSelectItem(desbaste.id)} /></td>
                                                            <td>{desbaste.numero}</td>
                                                            <td>{formatDate(desbaste.data)}</td>
                                                            <td>{formatNumber(desbaste.arvores_cortadas)}</td>
                                                            <td>{formatNumber(desbaste.lenha)}</td>
                                                            <td>{formatNumber(desbaste.toretes)}</td>
                                                            <td>{formatNumber(desbaste.toras_20_25cm)}</td>
                                                            <td>{formatNumber(desbaste.toras_25_33cm)}</td>
                                                            <td>{formatNumber(desbaste.toras_acima_33cm)}</td>
                                                            <td>{formatCurrency(desbaste.preco_lenha)}</td>
                                                            <td>{formatCurrency(desbaste.preco_toretes)}</td>
                                                            <td>{formatCurrency(desbaste.preco_toras_20_25cm)}</td>
                                                            <td>{formatCurrency(desbaste.preco_toras_25_33cm)}</td>
                                                            <td>{formatCurrency(desbaste.preco_toras_acima_33cm)}</td>
                                                            <td>{formatCurrency(desbaste.valor_extracao)}</td>
                                                            <td>{formatCurrency(totalLenha)}</td>
                                                            <td>{formatCurrency(totalToretes)}</td>
                                                            <td>{formatCurrency(totalToras2025cm)}</td>
                                                            <td>{formatCurrency(totalToras2533cm)}</td>
                                                            <td>{formatCurrency(totalTorasAcima33cm)}</td>
                                                            <td className={styles.totalGeralCell}>{formatCurrency(totalGeral)}</td>
                                                            <td>
                                                                <button className={styles.actionBtnDesbaste} onClick={() => confirmDelete(desbaste.id)} title="Excluir Registro">
                                                                    <FaTrashAlt />
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </section>
                        </main>
                     )}

                    <footer className={styles.desbasteModalFooter}>
                        <button type="button" className={`${styles.modalBtnDesbaste} ${styles.btnCancelDesbaste}`} onClick={onClose}>
                            Fechar
                        </button>
                        <button 
                            type="button" 
                            className={`${styles.modalBtnDesbaste} ${styles.btnSuccessDesbaste}`}
                            onClick={handleGenerateReport} 
                            disabled={loading || selectedItems.length === 0}
                        >
                           <FaFilePdf /> Gerar Relatório
                        </button>
                    </footer>
                </div>
            </div>
        </>
    );
};

export default DesbasteTablePopup;